import React, { useEffect, useState } from 'react';
import {
  UserMinus,
  Smartphone,
  Mail,
  Trash2,
  Archive,
  Timer,
  ShieldCheck,
  HelpCircle,
} from 'lucide-react';
import MainNavbar from '../../components/layout/MainNavbar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const supportEmail = 'support@trackyourwheelz.com';

const deletionSections = [
  {
    id: 'how-to-request',
    title: 'How to Request Account Deletion',
    description:
      'You can request account deletion using one of the following methods:',
    icon: UserMinus,
    subsections: [
      {
        label: 'Through the Mobile Application',
        items: [
          'Log in to the TrackYourWheelz mobile app.',
          'Navigate to Profile or Settings.',
          'Select Delete Account.',
          'Confirm your deletion request.',
        ],
        icon: Smartphone,
      },
      {
        label: 'By Contacting Support',
        description:
          'If you are unable to access the application, you can request account deletion by emailing our support team.',
        email: supportEmail,
        includeItems: [
          'Your registered email address',
          'Your full name',
          'Your company name (if applicable)',
        ],
        icon: Mail,
      },
    ],
  },
  {
    id: 'what-happens',
    title: 'What Happens When Your Account Is Deleted?',
    description:
      'Once your deletion request is verified and processed:',
    icon: Trash2,
    items: [
      'Your user profile will be permanently deleted.',
      'Your login credentials will be removed.',
      'Your personal information associated with the account will be deleted.',
      'Access to the TrackYourWheelz platform will be revoked.',
      'Any user-specific settings and preferences will be removed.',
    ],
  },
  {
    id: 'data-retained',
    title: 'Data That May Be Retained',
    description:
      'Certain information may be retained for a limited period where required for:',
    icon: Archive,
    items: [
      'Legal and regulatory compliance',
      'Fraud prevention and security investigations',
      'Financial and tax record requirements',
      'Dispute resolution',
    ],
    footer:
      'Retained information is securely stored and accessed only when required by law or legitimate business obligations.',
  },
  {
    id: 'processing-time',
    title: 'Processing Time',
    description:
      'Account deletion requests are typically processed within 30 days from the date of verification.',
    icon: Timer,
    footer:
      'You will receive a confirmation email once the deletion process has been completed.',
  },
];

const AccountDeletion = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  return (
    <>
      <div
        className={`min-h-screen relative overflow-hidden transition-colors duration-500 border-1 border-muted-foreground/20 ${
          theme === 'dark'
            ? 'bg-gradient-to-r from-[#483D8B] to-[#2d1b4e] text-slate-100'
            : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-slate-900'
        }`}
      >
        <MainNavbar theme={theme} toggleTheme={toggleTheme} activePage="Account Deletion" />

        <div className="absolute inset-0 pointer-events-none">
          {theme === 'dark' ? (
            <>
              <div className="absolute -top-24 -left-24 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl animate-pulse" />
              <div className="absolute top-1/3 right-10 w-72 h-72 bg-indigo-500/30 rounded-full blur-[140px] animate-pulse delay-200" />
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-blue-500/20 rounded-full blur-[160px] animate-pulse delay-500" />
            </>
          ) : (
            <>
              <div className="absolute -top-20 -left-20 w-96 h-96 bg-blue-200/40 rounded-full blur-3xl animate-pulse" />
              <div className="absolute top-1/2 right-0 w-80 h-80 bg-purple-200/40 rounded-full blur-3xl animate-pulse delay-200" />
              <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-indigo-200/40 rounded-full blur-3xl animate-pulse delay-500" />
            </>
          )}
        </div>

        <main className="relative z-10 pt-28 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-10">
            <header className="space-y-4">
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full ${
                  theme === 'dark'
                    ? 'bg-slate-900 border-1 border-muted-foreground/20 text-slate-100'
                    : 'bg-white border-1 border-muted-foreground/20 text-slate-700'
                }`}
              >
                <UserMinus className="h-4 w-4" />
                Account Deletion
              </div>
              <h1 className="text-sm sm:text-3xl font-bold">Delete Your TrackYourWheelz Account</h1>
              <p className={`text-sm sm:text-base ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                TrackYourWheelz provides users with the ability to request deletion of their account and associated
                personal data.
              </p>
            </header>

            <section
              className={`rounded-xl border-1 border-muted-foreground/20 ${
                theme === 'dark' ? 'bg-slate-900/60' : 'bg-white'
              } p-6 space-y-3`}
            >
              <h2 className="text-sm sm:text-base font-semibold flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-500" />
                Quick Overview
              </h2>
              <ul
                className={`list-disc ps-5 space-y-2 text-sm ${
                  theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                }`}
              >
                <li>
                  <span className="font-semibold">Self-service:</span> Delete your account directly from the mobile app
                  via Profile or Settings.
                </li>
                <li>
                  <span className="font-semibold">Support request:</span> Email our support team if you cannot access
                  the application.
                </li>
                <li>
                  <span className="font-semibold">Processing:</span> Requests are typically completed within 30 days
                  after verification.
                </li>
              </ul>
            </section>

            <section className="space-y-6">
              {deletionSections.map((section) => (
                <Card
                  key={section.id}
                  id={section.id}
                  className={`border ${
                    theme === 'dark'
                      ? 'border-1 border-muted-foreground/20 bg-slate-900/60'
                      : 'border-1 border-muted-foreground/20 bg-white'
                  }`}
                >
                  <CardHeader className="flex flex-row items-start gap-3 p-6 pb-3">
                    <div
                      className={`p-2 rounded-lg ${
                        theme === 'dark' ? 'bg-slate-800 text-slate-200' : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      <section.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-sm sm:text-lg font-semibold">{section.title}</CardTitle>
                      <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                        {section.description}
                      </p>
                    </div>
                  </CardHeader>
                  <CardContent
                    className={`p-6 pt-0 space-y-4 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}
                  >
                    {section.subsections ? (
                      section.subsections.map((subsection) => (
                        <div key={subsection.label} className="space-y-2">
                          <div className="flex items-center gap-2">
                            <subsection.icon className="h-4 w-4 text-blue-500" />
                            <p className="text-sm font-semibold">{subsection.label}</p>
                          </div>
                          {subsection.description && (
                            <p className="text-sm leading-relaxed ps-6">{subsection.description}</p>
                          )}
                          {subsection.email && (
                            <p className="text-sm leading-relaxed ps-6">
                              Email:{' '}
                              <a
                                href={`mailto:${subsection.email}`}
                                className="text-blue-500 hover:underline font-medium"
                              >
                                {subsection.email}
                              </a>
                            </p>
                          )}
                          {subsection.items && (
                            <ol
                              className={`list-decimal ps-10 space-y-1 text-sm ${
                                theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                              }`}
                            >
                              {subsection.items.map((item) => (
                                <li key={item}>{item}</li>
                              ))}
                            </ol>
                          )}
                          {subsection.includeItems && (
                            <div className="ps-6 space-y-1">
                              <p className="text-sm font-semibold">Please include:</p>
                              <ul className="list-disc ps-5 space-y-1 text-sm">
                                {subsection.includeItems.map((item) => (
                                  <li key={item}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <>
                        {section.items?.length > 0 && (
                          <ul className="list-disc ps-5 space-y-2 text-sm">
                            {section.items.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        )}
                        {section.footer && <p className="text-sm leading-relaxed pt-2">{section.footer}</p>}
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </section>

            <footer
              className={`rounded-xl border ${
                theme === 'dark'
                  ? 'border-1 border-muted-foreground/20 bg-slate-900/60'
                  : 'border-1 border-muted-foreground/20 bg-white'
              } p-6 space-y-4`}
            >
              <h2 className="text-sm sm:text-lg font-semibold flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-blue-500" />
                Need Assistance?
              </h2>
              <p className={`text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                If you have any questions regarding account deletion or data privacy, please contact us:
              </p>
              <div className={`text-sm space-y-1 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                <p className="font-semibold">TrackYourWheelz Support</p>
                <p>
                  Email:{' '}
                  <a href={`mailto:${supportEmail}`} className="text-blue-500 hover:underline font-medium">
                    {supportEmail}
                  </a>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => window.open(`mailto:${supportEmail}`, '_blank')}
                  className="w-full sm:w-48"
                  variant="outline"
                >
                  Email Support
                </Button>
                <Button
                  variant="outline"
                  onClick={() => document.getElementById('how-to-request')?.scrollIntoView({ behavior: 'smooth' })}
                  className="w-full sm:w-48"
                >
                  View Deletion Steps
                </Button>
              </div>
            </footer>
          </div>
        </main>
      </div>
    </>
  );
};

export default AccountDeletion;
